import { preserveBlockedSyncEvents } from "./sentry-integrations";
import { browserObservabilityOptions } from "@pima/observability/browser";
import * as Sentry from "@sentry/react";
import { makeAppTransport } from "./sentry-transport";

declare const __SENTRY_RELEASE__: string;

declare global {
  interface Window {
    __PIMA_SENTRY_READY__?: boolean;
  }
}

export function initializeSentry() {
  Sentry.init({
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
    release: __SENTRY_RELEASE__ || undefined,
    transport: makeAppTransport,

    // Add optional integrations for additional features
    //
    // Deliberately NOT using captureConsoleIntegration: src/lib/inspections/
    // sync-engine.ts runs an offline sync engine on 30s heartbeat + full-refresh
    // timers, and most of its console.error sites are reachable from those
    // loops (plus per-item retry loops within a single sync pass). Auto-
    // capturing every console.error would flood Sentry with duplicate events
    // for the same outage every tick. Instead, error sites outside that
    // polling surface (src/lib/api, src/lib/store, the react-query layer,
    // etc.) call Sentry.captureException explicitly at their catch blocks.
    integrations: (defaults) => [
      ...preserveBlockedSyncEvents(defaults),
      Sentry.replayIntegration({
        // Replay masks *text* by default but not URLs or media, and network
        // request/response *bodies* are only captured when a URL matches
        // networkDetailAllowUrls. Be explicit so a future reader can't assume
        // "default masking" still holds: mask all text, never capture media,
        // and capture no network bodies (they routinely carry PII — document
        // titles, patient/employee names — in this app).
        maskAllText: true,
        blockAllMedia: true,
        networkDetailAllowUrls: [],
      }),
    ],

    // Define how likely traces are sampled. Adjust this value in production, or use tracesSampler for greater control.
    environment: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT,
    tracesSampleRate: process.env.NODE_ENV === "production" ? 0.2 : 1.0,
    // Enable logs to be sent to Sentry
    enableLogs: true,

    // Define how likely Replay events are sampled.
    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0.1,

    // Define how likely Replay events are sampled when an error occurs.
    replaysOnErrorSampleRate: 1.0,

    ...browserObservabilityOptions(),
  });

  // The HTML boot reporter stands down after routes load and this client starts.
  window.__PIMA_SENTRY_READY__ = true;
}
// Device recovery keeps recent captured errors beside the inspection snapshot.
// This observer leaves the central reporting/filtering policy in place.
Sentry.addEventProcessor((event) => {
  if (
    event.type ||
    (event.level && !["error", "fatal", "warning"].includes(event.level))
  )
    return event;
  void Promise.all([
    import("./lib/inspections/db"),
    import("./lib/inspections/error-history"),
  ])
    .then(([{ db }, { recordInspectionError }]) =>
      recordInspectionError(db, {
        message: event.message,
        exception: event.exception,
        level: event.level,
        tags: event.tags,
        extra: event.extra,
        contexts: event.contexts,
        breadcrumbs: event.breadcrumbs,
      }),
    )
    .catch(() => {
      // A full or unavailable database must not prevent the original error report.
    });
  return event;
});
